// スマートガイド（整列補助線）の純粋ロジック。
//
// 移動・複製中のオブジェクト（複数選択時は外接矩形）の端／中心を、他オブジェクトの
// 端／中心と突き合わせ、しきい値以内で最も近いラインへスナップする量と、表示すべき
// 補助線（ガイド）を算出する。PowerPoint / FigJam のスマートガイドに相当する。
//
// 座標はすべてステージ座標（px）。React / DOM へ依存しない純粋関数のためテストしやすい。

/** 矩形（左上 + 幅高さ）。回転は考慮せず、データモデルの x/y/w/h をそのまま扱う。 */
export interface AlignmentBox {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** 表示する補助線 1 本。axis="x" は縦線（x 固定）、axis="y" は横線（y 固定）。 */
export interface AlignmentGuide {
  readonly axis: "x" | "y";
  /** 線の固定座標（axis="x" なら x、axis="y" なら y）。 */
  readonly position: number;
  /** 線分の開始座標（axis="x" なら y、axis="y" なら x）。 */
  readonly start: number;
  /** 線分の終了座標。 */
  readonly end: number;
}

export interface AlignmentResult {
  /** x 方向へ加算するスナップ量（揃わなければ 0）。 */
  readonly snapDx: number;
  /** y 方向へ加算するスナップ量（揃わなければ 0）。 */
  readonly snapDy: number;
  /** x 方向で整列が成立したか（成立軸はグリッド吸着より優先する判定に使う）。 */
  readonly matchedX: boolean;
  /** y 方向で整列が成立したか。 */
  readonly matchedY: boolean;
  /** 表示すべき補助線一覧。 */
  readonly guides: readonly AlignmentGuide[];
}

// 補助線へ巻き込む相手オブジェクトを拾う許容差（ステージ px）。
// スナップ後はベストペアが完全一致するため、整列ラインを共有する他オブジェクトを
// 1 本のガイドへまとめるための小さな値。
const GUIDE_COINCIDENCE_PX = 0.5;

const EMPTY_RESULT: AlignmentResult = {
  snapDx: 0,
  snapDy: 0,
  matchedX: false,
  matchedY: false,
  guides: [],
};

/** 矩形の縦ライン（x 値）3 本: 左端 / 中央 / 右端。 */
function verticalLines(box: AlignmentBox): readonly number[] {
  return [box.x, box.x + box.w / 2, box.x + box.w];
}

/** 矩形の横ライン（y 値）3 本: 上端 / 中央 / 下端。 */
function horizontalLines(box: AlignmentBox): readonly number[] {
  return [box.y, box.y + box.h / 2, box.y + box.h];
}

/**
 * 1 軸分のベストスナップを求める。
 * movingLines（移動側 3 本）と各 static の lines を総当たりし、|static - moving| が threshold
 * 以内で最小となるペアを選ぶ。返り値の delta は移動側へ加算するスナップ量、position は
 * 揃える先のライン座標（= 補助線を出す位置）。候補が無ければ null。
 */
function bestSnap(
  movingLines: readonly number[],
  staticLineGroups: readonly (readonly number[])[],
  threshold: number
): { readonly delta: number; readonly position: number } | null {
  let best: { delta: number; position: number } | null = null;
  let bestAbs = Infinity;
  for (const movingLine of movingLines) {
    for (const lines of staticLineGroups) {
      for (const staticLine of lines) {
        const delta = staticLine - movingLine;
        const abs = Math.abs(delta);
        if (abs <= threshold && abs < bestAbs) {
          bestAbs = abs;
          best = { delta, position: staticLine };
        }
      }
    }
  }
  return best;
}

/**
 * スナップ後の整列ライン（position）を通る補助線 1 本を組み立てる。
 * 線分の伸びは、移動矩形と「position にライン（端 / 中心）を持つ全 static 矩形」の
 * 垂直方向（axis に直交する方向）の合併範囲とし、整列している全オブジェクトを貫く線にする。
 */
function buildGuide(
  axis: "x" | "y",
  movedBox: AlignmentBox,
  statics: readonly AlignmentBox[],
  position: number
): AlignmentGuide {
  let start = axis === "x" ? movedBox.y : movedBox.x;
  let end = axis === "x" ? movedBox.y + movedBox.h : movedBox.x + movedBox.w;
  for (const s of statics) {
    const lines = axis === "x" ? verticalLines(s) : horizontalLines(s);
    const onLine = lines.some((line) => Math.abs(line - position) <= GUIDE_COINCIDENCE_PX);
    if (!onLine) continue;
    const sStart = axis === "x" ? s.y : s.x;
    const sEnd = axis === "x" ? s.y + s.h : s.x + s.w;
    start = Math.min(start, sStart);
    end = Math.max(end, sEnd);
  }
  return { axis, position, start, end };
}

/**
 * 移動矩形 moving を statics へ整列させるスナップ量と補助線を求める。
 *
 * @param moving 移動・複製中の矩形（複数選択時は外接矩形）。
 * @param statics 整列対象の他オブジェクト矩形（移動対象自身は呼び出し側で除外しておく）。
 * @param threshold スナップ判定のしきい値（ステージ px）。通常は screen px / zoom。
 */
export function computeAlignmentSnap(
  moving: AlignmentBox,
  statics: readonly AlignmentBox[],
  threshold: number
): AlignmentResult {
  if (statics.length === 0 || threshold <= 0) return EMPTY_RESULT;

  const staticVertical = statics.map((s) => verticalLines(s));
  const staticHorizontal = statics.map((s) => horizontalLines(s));

  const snapX = bestSnap(verticalLines(moving), staticVertical, threshold);
  const snapY = bestSnap(horizontalLines(moving), staticHorizontal, threshold);

  if (!snapX && !snapY) return EMPTY_RESULT;

  const snapDx = snapX?.delta ?? 0;
  const snapDy = snapY?.delta ?? 0;
  // スナップ後の矩形位置でガイドの線分範囲を確定する。
  const movedBox: AlignmentBox = { ...moving, x: moving.x + snapDx, y: moving.y + snapDy };
  const guides: AlignmentGuide[] = [];
  if (snapX) guides.push(buildGuide("x", movedBox, statics, snapX.position));
  if (snapY) guides.push(buildGuide("y", movedBox, statics, snapY.position));

  return {
    snapDx,
    snapDy,
    matchedX: snapX !== null,
    matchedY: snapY !== null,
    guides,
  };
}
